<script setup lang="ts">
import type { Settlement } from '../internal/types.ts'
import { ActionButton, Dialog, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { isValidShareCode, normalizeShareCode } from '../internal/share-code.ts'
import RelationshipIcon from '@/features/relationships/components/RelationshipIcon.vue'
import { type ShareCodeFailure, shareCodeFailure, useLedgerCode } from '../composables/use-ledger-code.ts'
import { isProposalToSelf, useProposeRelationship } from '../composables/use-propose-relationship.ts'

const props = defineProps<{ open: boolean; sourceLedgerId: string; sourceLedgerName: string }>()
const emit = defineEmits<{ close: []; created: [] }>()

const { t } = useI18n()
const ledgerCode = useLedgerCode()
const propose = useProposeRelationship()

const code = ref('')
const settlement = ref<Settlement>('CONTRIBUTION')
const preview = ref<{ code: string; name: string } | undefined>(undefined)
const lookupFailure = ref<ShareCodeFailure | 'invalid' | undefined>(undefined)
const isLookingUp = ref(false)
const sendFailure = ref<'self' | 'failed' | undefined>(undefined)

const codeInvalid = computed(() => code.value.length > 0 && !isValidShareCode(code.value))
const codeHint = computed(() =>
  codeInvalid.value ? t('relationships.codeInvalid') : t('relationships.codeHint'),
)

const failureText = computed(() => {
  if (lookupFailure.value === 'invalid') return t('relationships.codeInvalid')
  if (lookupFailure.value === 'not-found') return t('relationships.codeNotFound')
  if (lookupFailure.value === 'rate-limited') return t('relationships.codeRateLimited')
  if (lookupFailure.value === 'failed') return t('relationships.codeLookupFailed')
  return undefined
})

function reset(): void {
  code.value = ''
  settlement.value = 'CONTRIBUTION'
  preview.value = undefined
  lookupFailure.value = undefined
  sendFailure.value = undefined
}

watch(
  () => props.open,
  (open) => {
    if (!open) reset()
  },
)

function onCodeInput(event: Event): void {
  const input = event.target as HTMLInputElement
  const next = normalizeShareCode(input.value)
  input.value = next
  if (next === code.value) return
  code.value = next
  preview.value = undefined
  lookupFailure.value = undefined
  sendFailure.value = undefined
}

async function findLedger(): Promise<void> {
  if (!isValidShareCode(code.value)) {
    lookupFailure.value = 'invalid'
    return
  }
  const requested = code.value
  isLookingUp.value = true
  lookupFailure.value = undefined
  try {
    const found = await ledgerCode.lookup(requested)
    if (code.value !== requested) return
    preview.value = { code: requested, name: found.name }
  } catch (caught) {
    if (code.value !== requested) return
    lookupFailure.value = shareCodeFailure(caught)
  } finally {
    isLookingUp.value = false
  }
}

async function sendRequest(): Promise<void> {
  if (preview.value === undefined) return
  const sent = preview.value.code
  sendFailure.value = undefined
  try {
    await propose.propose(props.sourceLedgerId, {
      targetLedgerCode: sent,
      settlement: settlement.value,
    })
    emit('created')
    emit('close')
  } catch (caught) {
    if (code.value !== sent) return
    if (isProposalToSelf(caught)) {
      sendFailure.value = 'self'
      return
    }
    const failure = shareCodeFailure(caught)
    if (failure === 'failed') {
      sendFailure.value = 'failed'
      return
    }
    lookupFailure.value = failure
    preview.value = undefined
  }
}
</script>

<template>
  <Dialog :open="open" :label="t('relationships.connectTitle')" @close="emit('close')">
    <form class="flex flex-col gap-[var(--space-block)] p-[var(--space-block)]" @submit.prevent="findLedger">
      <header class="flex flex-col gap-1">
        <h2
          class="flex items-center gap-2 text-[length:var(--text-step-5)] font-semibold text-[color:var(--color-text)]"
        >
          <RelationshipIcon name="link" />
          {{ t('relationships.connectTitle') }}
        </h2>
        <p class="text-[color:var(--color-text-muted)]">{{ t('relationships.connectIntro') }}</p>
      </header>

      <div class="flex flex-col gap-1">
        <label for="connect-ledger-code" class="font-medium text-[color:var(--color-text)]">
          {{ t('relationships.codeLabel') }}
        </label>
        <div class="flex flex-wrap items-center gap-[var(--space-inline)]">
          <input
            id="connect-ledger-code"
            :value="code"
            type="text"
            inputmode="text"
            autocomplete="off"
            spellcheck="false"
            maxlength="7"
            required
            class="field-control w-40 font-mono text-[length:var(--text-step-4)] tracking-[0.2em] uppercase"
            :aria-invalid="codeInvalid ? 'true' : undefined"
            aria-describedby="connect-ledger-code-hint"
            @input="onCodeInput"
          />
          <ActionButton
            variant="secondary"
            :loading="isLookingUp"
            :disabled="code.length === 0"
            @click="findLedger"
          >
            {{ t('relationships.findLedger') }}
          </ActionButton>
        </div>
        <p id="connect-ledger-code-hint" class="text-[color:var(--color-text-muted)]">{{ codeHint }}</p>
      </div>

      <div v-if="failureText" role="alert">
        <StatusTag tone="danger">{{ failureText }}</StatusTag>
      </div>

      <section
        v-if="preview !== undefined"
        class="flex flex-col gap-[var(--space-block)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface-raised)] p-[var(--space-cell)]"
        :aria-label="t('relationships.previewTitle')"
      >
        <p class="text-[color:var(--color-text)]">
          {{ t('relationships.previewLine', { ledger: preview.name }) }}
        </p>

        <fieldset class="flex flex-col gap-2">
          <legend class="font-medium text-[color:var(--color-text)]">
            {{ t('relationships.settlement') }}
          </legend>
          <label class="flex items-start gap-2 text-[color:var(--color-text)]">
            <input v-model="settlement" type="radio" value="CONTRIBUTION" class="mt-1" />
            <span>
              {{ t('relationships.settlementContribution') }}
              <span class="block text-[color:var(--color-text-muted)]">
                {{ t('relationships.settlementContributionHint') }}
              </span>
            </span>
          </label>
          <label class="flex items-start gap-2 text-[color:var(--color-text)]">
            <input v-model="settlement" type="radio" value="REIMBURSEMENT" class="mt-1" />
            <span>
              {{ t('relationships.settlementReimbursement') }}
              <span class="block text-[color:var(--color-text-muted)]">
                {{ t('relationships.settlementReimbursementHint') }}
              </span>
            </span>
          </label>
        </fieldset>

        <p class="text-[color:var(--color-text-muted)]">
          {{
            t('relationships.disclosure', {
              ledger: preview.name,
              source: props.sourceLedgerName,
            })
          }}
        </p>
      </section>

      <StatusTag v-if="sendFailure === 'self'" tone="danger">
        {{ t('relationships.proposeToSelf') }}
      </StatusTag>
      <StatusTag v-else-if="sendFailure === 'failed'" tone="danger">
        {{ t('relationships.proposeFailed') }}
      </StatusTag>

      <footer class="flex flex-wrap justify-end gap-[var(--space-inline)]">
        <ActionButton variant="quiet" @click="emit('close')">{{ t('relationships.close') }}</ActionButton>
        <ActionButton
          v-if="preview !== undefined"
          variant="primary"
          :loading="propose.isSubmitting.value"
          @click="sendRequest"
        >
          <RelationshipIcon name="send" />
          {{ t('relationships.propose') }}
        </ActionButton>
      </footer>
    </form>
  </Dialog>
</template>
